"use client";

import { useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useConvexAuth, useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { isOrgAdmin } from "@shared/auth/roles";
import { extractEmailDomain, isGenericEmailDomain } from "@shared/auth/generic-email-domains";
import { Button } from "@/components/ui/button";
import type { StatedAccountUses } from "@shared/auth/account-audience";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { revealFirstInvalidField } from "@/lib/motion";
import { AuthField } from "./auth-field";
import { useVisibleErrors, type VisibleErrors } from "./use-visible-errors";
import { AuthError } from "./auth-messages";

export interface AboutYouResult {
  intent?: StatedAccountUses;
  joinRequested: boolean;
}

interface AboutValues {
  firstName: string;
  lastName: string;
  company: string;
  justMe: boolean;
}

function companyNameFromEmail(email: string): string {
  if (isGenericEmailDomain(email)) return "";
  const label = extractEmailDomain(email)?.split(".")[0] ?? "";
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function useAboutAccount() {
  const auth = useConvexAuth();
  const current = useQuery(api.companies.queries.getCurrentUserWithCompany, auth.isAuthenticated ? {} : "skip");
  const email = current?.user.email ?? "";
  const match = useQuery(
    api.companies.queries.byEmailDomain.findCompanyByEmailDomain,
    email && !isGenericEmailDomain(email) ? {} : "skip",
  );
  return { current, match: match ?? null };
}

const CHOICE_CARD =
  "flex min-h-14 cursor-pointer items-start gap-3 rounded-md border border-border bg-card px-4 py-3 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-action-tint";

function CompanyChoice(props: {
  value: "join" | "other";
  label: string;
  detail?: string;
}) {
  const id = useId();
  return (
    <label htmlFor={id} className={CHOICE_CARD}>
      <RadioGroupItem id={id} value={props.value} className="mt-0.5" />
      <span className="space-y-0.5">
        <span className="block text-sm font-semibold text-foreground">{props.label}</span>
        {props.detail ? <span className="block text-sm text-muted-foreground">{props.detail}</span> : null}
      </span>
    </label>
  );
}

type AboutFieldErrors = VisibleErrors<keyof Pick<AboutValues, "firstName" | "lastName" | "company">>;

function CompanyFields(props: {
  values: AboutValues;
  set: (patch: Partial<AboutValues>) => void;
  fieldErrors: AboutFieldErrors;
}) {
  const id = useId();
  return (
    <div className="space-y-2">
      {props.values.justMe ? null : (
        <AuthField
          label="Your company"
          autoComplete="organization"
          value={props.values.company}
          onChange={(company) => props.set({ company })}
          error={props.fieldErrors.visible("company")}
          onBlur={() => props.fieldErrors.touch("company")}
        />
      )}
      <div className="flex min-h-11 items-center gap-2">
        <Checkbox id={id} checked={props.values.justMe} onCheckedChange={(value) => props.set({ justMe: value === true })} />
        <Label htmlFor={id} className="font-normal">I&apos;m not with a company</Label>
      </div>
    </div>
  );
}

function initialValues(current: ReturnType<typeof useAboutAccount>["current"]): AboutValues {
  const email = current?.user.email ?? "";
  const company = current?.company;
  const madeUpName = company?.entityType === "individual";
  return {
    firstName: current?.user.firstName ?? "",
    lastName: current?.user.lastName ?? "",
    company: company && !madeUpName ? company.name : companyNameFromEmail(email),
    justMe: false,
  };
}

function useSaveAbout(current: ReturnType<typeof useAboutAccount>["current"], intent: StatedAccountUses | undefined) {
  const updateUser = useMutation(api.users.mutations.updateProfile);
  const setEntityType = useMutation(api.companies.mutations.setEntityType.setEntityType);
  const setStatedUses = useMutation(api.companies.mutations.statedUses.setStatedUses);
  const requestToJoin = useMutation(api.companies.mutations.joinExistingCompany.requestToJoinCompany);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const save = async (values: AboutValues, joinCompanyId: Id<"companies"> | null): Promise<boolean> => {
    setIsSaving(true);
    setError(null);
    try {
      await updateUser({ firstName: values.firstName.trim(), lastName: values.lastName.trim() });
      const company = current?.company;
      const companyName = values.justMe || joinCompanyId ? "" : values.company.trim();
      const personName = `${values.firstName.trim()} ${values.lastName.trim()}`;
      if (company && isOrgAdmin(company.role)) {
        const named = await setEntityType({
          companyId: company._id,
          entityType: companyName ? "business" : "individual",
          displayName: companyName || personName,
        });
        if (!named.success) throw new Error(named.error ?? "Company not saved");
      }
      if (joinCompanyId) {
        await requestToJoin({ companyId: joinCompanyId });
      } else if (intent && company && isOrgAdmin(company.role)) {
        const stated = await setStatedUses({ companyId: company._id, ...intent });
        if (!stated.success) throw new Error(stated.error ?? "Uses not saved");
      }
      return true;
    } catch {
      setError("We couldn't save that. Please try again.");
      return false;
    } finally {
      setIsSaving(false);
    }
  };
  return { error, isSaving, save };
}

function validate(values: AboutValues, joining: boolean) {
  return {
    ...(values.firstName.trim() ? {} : { firstName: "Enter your first name" }),
    ...(values.lastName.trim() ? {} : { lastName: "Enter your last name" }),
    ...(joining || values.justMe || values.company.trim()
      ? {}
      : { company: "Enter your company name, or tick “I’m not with a company”" }),
  };
}

function NameFields(props: {
  values: AboutValues;
  set: (patch: Partial<AboutValues>) => void;
  errors: AboutFieldErrors;
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <AuthField
        label="First name"
        autoComplete="given-name"
        autoFocus
        value={props.values.firstName}
        onChange={(firstName) => props.set({ firstName })}
        error={props.errors.visible("firstName")}
        onBlur={() => props.errors.touch("firstName")}
      />
      <AuthField
        label="Last name"
        autoComplete="family-name"
        value={props.values.lastName}
        onChange={(lastName) => props.set({ lastName })}
        error={props.errors.visible("lastName")}
        onBlur={() => props.errors.touch("lastName")}
      />
    </div>
  );
}

export function AboutYouStep(props: {
  intent?: StatedAccountUses;
  onDone: (result: AboutYouResult) => void;
}) {
  const { current, match } = useAboutAccount();
  const [edited, setEdited] = useState<AboutValues | null>(null);
  const [join, setJoin] = useState(true);
  const details = useSaveAbout(current, props.intent);
  const formRef = useRef<HTMLFormElement>(null);
  const values = edited ?? initialValues(current);
  const joining = match !== null && join;
  const fieldErrors = useVisibleErrors(validate(values, joining));
  if (current === undefined) return <p className="text-sm text-muted-foreground">Loading your account…</p>;
  const set = (patch: Partial<AboutValues>) => setEdited({ ...values, ...patch });
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    fieldErrors.attempt();
    if (fieldErrors.hasErrors) {
      revealFirstInvalidField(formRef.current);
      return;
    }
    const saved = await details.save(values, joining ? match.companyId : null);
    if (saved) props.onDone({ intent: joining ? undefined : props.intent, joinRequested: joining });
  };
  return (
    <form ref={formRef} className="space-y-5" onSubmit={submit} noValidate>
      <NameFields values={values} set={set} errors={fieldErrors} />
      <CompanySection match={match} join={join} onJoinChange={setJoin} values={values} set={set} fieldErrors={fieldErrors} />
      <AuthError message={details.error} />
      <Button type="submit" variant="primary" className="w-full" disabled={details.isSaving}>
        {details.isSaving ? "Setting up your account…" : "Create account"}
      </Button>
    </form>
  );
}

function CompanySection(props: {
  match: { companyName: string } | null;
  join: boolean;
  onJoinChange: (join: boolean) => void;
  values: AboutValues;
  set: (patch: Partial<AboutValues>) => void;
  fieldErrors: AboutFieldErrors;
}): ReactNode {
  if (!props.match) return <CompanyFields values={props.values} set={props.set} fieldErrors={props.fieldErrors} />;
  return (
    <fieldset className="space-y-2">
      <legend className="mb-2 text-sm font-medium text-foreground">Your company</legend>
      <RadioGroup value={props.join ? "join" : "other"} onValueChange={(value) => props.onJoinChange(value === "join")}>
        <CompanyChoice
          value="join"
          label={`Join ${props.match.companyName}`}
          detail="Your colleagues already use Repackify. An admin there approves your request."
        />
        <CompanyChoice value="other" label="Set up a different company" />
      </RadioGroup>
      {props.join ? null : <CompanyFields values={props.values} set={props.set} fieldErrors={props.fieldErrors} />}
    </fieldset>
  );
}
